/**
 * Password reset (migration flow) contracts — E01-F03. Shared by api and web
 * (architecture.md §4: contracts are zod schemas in @eer/shared, shared by both
 * sides; §9 "Пароли" — argon2 + forced reset for migrated plaintext users).
 *
 * NEW in v2: the legacy n8n app had NO reset flow (passwords were plain text,
 * compared directly in SQL — docs/source-n8n/reference/Users.md). Migrated users
 * whose plaintext could not be re-hashed to argon2 carry `password_reset_required`
 * and are walked through:
 *
 *   POST /auth/request-reset  { email }                     → 202 always (no enum)
 *   POST /auth/reset-password { token, new_password }       → { success: true }
 *
 * Anti-enumeration parity with login: `request-reset` returns the SAME body for a
 * known and an unknown email so the endpoint never reveals whether an email
 * exists.
 *
 * Schemas are the single source of truth — every request body and response body
 * is `z.infer`-derived so the route validator and the web client can never drift.
 * Web never imports `zod`; it consumes these schemas via @eer/shared.
 *
 * This file is feature-owned and is re-exported from `@eer/shared` by the Wire
 * pass (it must NOT edit the shared barrel itself during parallel work).
 */
import { z } from 'zod';
/**
 * Special error code surfaced when an authenticated principal still has
 * `password_reset_required = true`. It is intentionally distinct from a plain
 * `UNAUTHENTICATED` (401) so the SPA can branch to the reset flow instead of the
 * login screen (T04 acceptance: "ответ явно указывает на необходимость сброса").
 * Carried in the unified error body as `error.code = 'FORBIDDEN'` with this
 * marker in `error.details.reason`, so it rides on the existing error contract
 * without adding a new top-level code.
 */
export const PASSWORD_RESET_REQUIRED_REASON = 'password_reset_required';
/**
 * Type guard: does this (unknown) error-detail payload indicate the
 * password-reset gate? Used by the web client to route to the reset view rather
 * than the login screen.
 */
export function isPasswordResetRequiredDetails(details) {
    return (typeof details === 'object' &&
        details !== null &&
        details.reason === PASSWORD_RESET_REQUIRED_REASON);
}
/** `POST /auth/request-reset` request body — only an email. */
export const requestResetBodySchema = z.object({
    email: z.string().trim().min(1, 'Email is required').max(255),
});
/**
 * `POST /auth/request-reset` response — a fixed acknowledgement, identical for a
 * known and unknown email (anti-enumeration). The optional `reset_token` is
 * populated ONLY in non-production / test mode (no email provider wired) so the
 * e2e flow can complete; it is NEVER set in production.
 */
export const requestResetResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.string().min(1),
    reset_token: z.string().min(1).optional(),
});
/**
 * Minimum new-password policy (parity-plus: the legacy app had no policy at all).
 * Kept deliberately small — the goal is "not a legacy plaintext", not a corporate
 * password policy.
 */
export const NEW_PASSWORD_MIN_LENGTH = 8;
export const NEW_PASSWORD_MAX_LENGTH = 128;
/** `POST /auth/reset-password` request body — the one-time token + new password. */
export const resetPasswordBodySchema = z.object({
    token: z.string().trim().min(1, 'Token is required').max(255),
    new_password: z
        .string()
        .min(NEW_PASSWORD_MIN_LENGTH, `Password must be at least ${NEW_PASSWORD_MIN_LENGTH} characters long`)
        .max(NEW_PASSWORD_MAX_LENGTH),
});
/** `POST /auth/reset-password` success response. */
export const resetPasswordResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.string().min(1),
});
//# sourceMappingURL=password-reset.js.map